import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Clock, Mail, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Full Name", type: "text", ph: "Alex Rivera" },
  { name: "email", label: "Email", type: "email", ph: "alex@email.com" },
  { name: "subject", label: "Subject", type: "text", ph: "Catering enquiry" },
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent!", { description: "We'll get back to you shortly." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-beige py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-teal-brand">Say Hello</p>
            <h2 className="mt-3 font-display text-4xl sm:text-6xl font-bold tracking-tight text-coffee">Come refuel with us.</h2>
            <p className="mt-4 text-lg text-coffee-soft">Questions, catering or just want to visit? Drop us a line — the machine never sleeps, and neither does our community.</p>
          </Reveal>

          <div className="mt-10 space-y-5">
            {[
              [MapPin, "Visit", "12 Fitness Lane, Indiranagar, Bengaluru 560038"],
              [Clock, "Hours", "Cafe 7am–10pm · Vending 24/7"],
              [Mail, "Email", "hello@proteincafe.in"],
            ].map(([Icon, label, val], i) => (
              <Reveal key={label} delay={i} className="flex items-start gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-beige text-teal-brand shrink-0"><Icon size={19} /></span>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-coffee-muted font-bold">{label}</div>
                  <div className="text-coffee font-medium">{val}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal className="rounded-[2.5rem] border border-line p-8 sm:p-10 bg-white relative">
            <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-6">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.name === "subject" || f.name === "phone" ? "" : ""}>
                  <label className="block text-xs uppercase tracking-[0.15em] text-coffee-muted font-bold mb-2">{f.label}</label>
                  <input
                    data-testid={`contact-${f.name}`}
                    type={f.type}
                    value={form[f.name]}
                    onChange={set(f.name)}
                    placeholder={f.ph}
                    className="w-full bg-transparent border-b border-coffee/25 focus:border-teal-brand outline-none py-2.5 text-coffee placeholder:text-coffee-muted/60 transition-colors"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.15em] text-coffee-muted font-bold mb-2">Message</label>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Tell us what's on your mind…"
                  className="w-full bg-transparent border-b border-coffee/25 focus:border-teal-brand outline-none py-2.5 text-coffee placeholder:text-coffee-muted/60 resize-none transition-colors"
                />
              </div>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#8A2231] hover:bg-[#6E1926] disabled:opacity-60 text-white font-semibold py-4 transition-colors"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
